import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
    Card, CardBody, Row, Col, Table, Input,
    Accordion, AccordionItem, AccordionHeader, AccordionBody
} from "reactstrap";
import styles from "./style.module.scss";
import cx from "classnames";
import isEqual from "lodash.isequal";
import { type RouteCategory } from "@/security/routes.catalog";

export type ChecklistValues = Record<string, boolean>;

export type ChecklistNode = {
    id: string;
    label: string;
    category: RouteCategory;
    /** Valor por defecto propio del nodo (tiene prioridad sobre defaultValue) */
    defaultAllow?: boolean;
};

type Props = {
    nodes: ChecklistNode[];
    initialValues?: ChecklistValues;
    onChange?: (values: ChecklistValues) => void;
    readOnly?: boolean;
    /** Valor por defecto cuando el nodo no está en initialValues ni define defaultAllow */
    defaultValue?: boolean;
    /** Habilita una columna extra para marcar funciones como "principal" (se muestran primero). */
    enablePrincipal?: boolean;
    /** Funciones marcadas como principal (featureId -> true). */
    initialPrincipal?: ChecklistValues;
    /** Notifica los cambios en las funciones principales. */
    onPrincipalChange?: (values: ChecklistValues) => void;
    /** Habilita una columna extra para marcar funciones como "ocultas" (no se muestran en los planes). */
    enableHidden?: boolean;
    /** Funciones marcadas como ocultas (featureId -> true). */
    initialHidden?: ChecklistValues;
    /** Notifica los cambios en las funciones ocultas. */
    onHiddenChange?: (values: ChecklistValues) => void;
};

const CATEGORY_LABEL: Record<RouteCategory, string> = {
    configuration: "Configuración",
    inventory: "Inventario",
    clients: "Clientes",
    providers: "Proveedores",
    invoicing: "Facturación",
    accounting: "Contabilidad",
};

export default function FeatureChecklistEditor({ nodes, initialValues, onChange, readOnly, defaultValue = false, enablePrincipal = false, initialPrincipal, onPrincipalChange, enableHidden = false, initialHidden, onHiddenChange }: Props) {
    // Agrupar catálogo por categoría y ordenar por label
    const groupedByCategory: Map<RouteCategory, ChecklistNode[]> = useMemo(() => {
        const map = new Map<RouteCategory, ChecklistNode[]>();
        for (const item of nodes) {
            const arr = map.get(item.category) ?? [];
            arr.push(item);
            map.set(item.category, arr);
        }
        for (const [k, arr] of map.entries()) {
            arr.sort((a, b) => a.label.localeCompare(b.label));
            map.set(k, arr);
        }
        return map;
    }, [nodes]);

    // IDs de categorías, ordenadas por nombre de categoría
    const catIds: string[] = useMemo(() => {
        return [...groupedByCategory.keys()]
            .sort((a, b) => CATEGORY_LABEL[a].localeCompare(CATEGORY_LABEL[b]))
            .map(c => c as string);
    }, [groupedByCategory]);

    // Valores iniciales normalizados (deep-stable)
    const computedInitial: ChecklistValues = useMemo(() => {
        const base: ChecklistValues = {};
        for (const n of nodes) {
            base[n.id] =
                initialValues?.[n.id] ??
                (typeof n.defaultAllow === "boolean" ? n.defaultAllow : defaultValue);
        }
        return base;
    }, [nodes, initialValues, defaultValue]);

    const [values, setValues] = useState<ChecklistValues>(computedInitial);

    // Sincronizar solo si realmente cambió (evita bucles de props -> state -> props)
    useEffect(() => {
        if (!isEqual(values, computedInitial)) {
            setValues(computedInitial);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [computedInitial]);

    // Estado de funciones principales (solo relevante cuando enablePrincipal)
    const computedInitialPrincipal: ChecklistValues = useMemo(() => {
        const base: ChecklistValues = {};
        for (const n of nodes) {
            base[n.id] = initialPrincipal?.[n.id] ?? false;
        }
        return base;
    }, [nodes, initialPrincipal]);

    const [principal, setPrincipal] = useState<ChecklistValues>(computedInitialPrincipal);

    useEffect(() => {
        if (!isEqual(principal, computedInitialPrincipal)) {
            setPrincipal(computedInitialPrincipal);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [computedInitialPrincipal]);

    const lastEmittedPrincipalRef = useRef<ChecklistValues>(principal);
    const onPrincipalChangeRef = useRef(onPrincipalChange);
    useEffect(() => { onPrincipalChangeRef.current = onPrincipalChange; }, [onPrincipalChange]);

    useEffect(() => {
        if (!isEqual(principal, lastEmittedPrincipalRef.current)) {
            lastEmittedPrincipalRef.current = principal;
            onPrincipalChangeRef.current?.(principal);
        }
    }, [principal]);

    const togglePrincipal = useCallback((id: string, val: boolean) => {
        setPrincipal(prev => {
            if (prev[id] === val) return prev;
            return { ...prev, [id]: val };
        });
    }, []);

    // Estado de funciones ocultas (solo relevante cuando enableHidden)
    const computedInitialHidden: ChecklistValues = useMemo(() => {
        const base: ChecklistValues = {};
        for (const n of nodes) {
            base[n.id] = initialHidden?.[n.id] ?? false;
        }
        return base;
    }, [nodes, initialHidden]);

    const [hidden, setHidden] = useState<ChecklistValues>(computedInitialHidden);

    useEffect(() => {
        if (!isEqual(hidden, computedInitialHidden)) {
            setHidden(computedInitialHidden);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [computedInitialHidden]);

    const lastEmittedHiddenRef = useRef<ChecklistValues>(hidden);
    const onHiddenChangeRef = useRef(onHiddenChange);
    useEffect(() => { onHiddenChangeRef.current = onHiddenChange; }, [onHiddenChange]);

    useEffect(() => {
        if (!isEqual(hidden, lastEmittedHiddenRef.current)) {
            lastEmittedHiddenRef.current = hidden;
            onHiddenChangeRef.current?.(hidden);
        }
    }, [hidden]);

    const toggleHidden = useCallback((id: string, val: boolean) => {
        setHidden(prev => {
            if (prev[id] === val) return prev;
            return { ...prev, [id]: val };
        });
    }, []);

    // Emitir onChange solo cuando values cambia de verdad
    const lastEmittedRef = useRef<ChecklistValues>(values);
    const onChangeRef = useRef(onChange);
    useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

    useEffect(() => {
        if (!isEqual(values, lastEmittedRef.current)) {
            lastEmittedRef.current = values;
            onChangeRef.current?.(values);
        }
    }, [values]);

    // Acordeones: abiertos por defecto todas las categorías
    const [openCats, setOpenCats] = useState<string[]>(catIds);
    useEffect(() => setOpenCats(catIds), [catIds]);

    const toggleCat = useCallback((id: string) => {
        setOpenCats(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
    }, []);

    const setValue = useCallback((id: string, val: boolean) => {
        setValues(prev => {
            if (prev[id] === val) return prev; // evita renders innecesarios
            return { ...prev, [id]: val };
        });
    }, []);

    const setCategoryAll = useCallback((cat: RouteCategory, val: boolean) => {
        const ids = groupedByCategory.get(cat)?.map(n => n.id) ?? [];
        setValues(prev => {
            const next = { ...prev };
            let changed = false;
            for (const id of ids) {
                if (next[id] !== val) {
                    next[id] = val;
                    changed = true;
                }
            }
            return changed ? next : prev;
        });
    }, [groupedByCategory]);

    const catStats = useCallback((cat: RouteCategory) => {
        const items = groupedByCategory.get(cat) ?? [];
        const total = items.length;
        const enabled = items.filter(n => values[n.id]).length;
        return { enabled, total, all: enabled === total, none: enabled === 0 };
    }, [groupedByCategory, values]);

    return (
        <Row className="g-3">
            <Col md="12">
                <Accordion open={openCats} toggle={toggleCat}>
                    {catIds.map(cat => {
                        const isOpen = openCats.includes(cat);
                        const catKey = cat as RouteCategory;
                        const items = groupedByCategory.get(catKey)!;
                        const { all, none } = catStats(catKey);
                        const indeterminate = !(all || none);

                        return (
                            <AccordionItem key={cat}>
                                <AccordionHeader targetId={cat}>
                                    <div className="d-flex align-items-center gap-3 w-100">
                                        <strong className="me-auto">{CATEGORY_LABEL[catKey]}</strong>
                                        <div
                                            className="d-flex align-items-center gap-2"
                                            onClick={e => e.stopPropagation()}
                                        >
                                            {/* Checkbox maestro con indeterminate */}
                                            <MasterCheckbox
                                                checked={all}
                                                indeterminate={indeterminate}
                                                onChange={e => setCategoryAll(catKey, e.target.checked)}
                                                disabled={readOnly}
                                                className={cx({ [styles.openInput]: isOpen && !all })}
                                                title="Permitir todo / nada en esta categoría"
                                            />
                                            <span className="text-muted small">Permitir todo</span>
                                        </div>
                                    </div>
                                </AccordionHeader>
                                <AccordionBody accordionId={cat}>
                                    <Card className="mb-3">
                                        <CardBody className="p-0">
                                            <Table responsive hover className="mb-0 align-middle">
                                                <thead>
                                                    <tr>
                                                        <th style={{ width: 56 }} className="text-center">Permitido</th>
                                                        {enablePrincipal && (
                                                            <th style={{ width: 72 }} className="text-center">Principal</th>
                                                        )}
                                                        {enableHidden && (
                                                            <th style={{ width: 72 }} className="text-center">Oculto</th>
                                                        )}
                                                        <th>Acción</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {items.map(n => (
                                                        <tr key={n.id}>
                                                            <td className="text-center">
                                                                <Input
                                                                    type="checkbox"
                                                                    checked={!!values[n.id]}
                                                                    onChange={e => setValue(n.id, e.target.checked)}
                                                                    disabled={readOnly}
                                                                    aria-label={`Permitir ${n.label}`}
                                                                />
                                                            </td>
                                                            {enablePrincipal && (
                                                                <td className="text-center">
                                                                    <Input
                                                                        type="checkbox"
                                                                        checked={!!principal[n.id]}
                                                                        onChange={e => togglePrincipal(n.id, e.target.checked)}
                                                                        disabled={readOnly || !values[n.id]}
                                                                        aria-label={`Marcar ${n.label} como principal`}
                                                                    />
                                                                </td>
                                                            )}
                                                            {enableHidden && (
                                                                <td className="text-center">
                                                                    <Input
                                                                        type="checkbox"
                                                                        checked={!!hidden[n.id]}
                                                                        onChange={e => toggleHidden(n.id, e.target.checked)}
                                                                        disabled={readOnly || !values[n.id]}
                                                                        aria-label={`Ocultar ${n.label}`}
                                                                    />
                                                                </td>
                                                            )}
                                                            <td>{n.label}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        </CardBody>
                                    </Card>
                                </AccordionBody>
                            </AccordionItem>
                        );
                    })}
                </Accordion>
            </Col>
        </Row>
    );
}

/** Checkbox que soporta estado indeterminate sin generar hooks dentro de un map */
function MasterCheckbox({
    checked,
    indeterminate,
    onChange,
    disabled,
    className,
    title,
}: {
    checked: boolean;
    indeterminate: boolean;
    onChange: React.ChangeEventHandler<HTMLInputElement>;
    disabled?: boolean;
    className?: string;
    title?: string;
}) {
    const ref = useRef<HTMLInputElement>(null);
    useEffect(() => {
        if (ref.current) ref.current.indeterminate = indeterminate;
    }, [indeterminate]);
    return (
        <Input
            innerRef={ref}
            type="checkbox"
            checked={checked}
            onChange={onChange}
            disabled={disabled}
            className={className}
            title={title}
        />
    );
}
